/**
 * DesdeBalance.tsx — subir el balance de comprobación del sistema ANTERIOR.
 *
 * Es la otra mitad de `Apertura.tsx`. La plantilla nuestra sirve para quien
 * está dispuesto a traducir sus cuentas a mano en una hoja de cálculo; esta
 * pantalla existe para que no haga falta, que es de lo que se trataba: el
 * cliente exporta su balance tal como su sistema lo da y dice acá, una sola
 * vez, a qué cuenta nuestra corresponde cada una de las suyas.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ LA SUGERENCIA LLEGA VACÍA CUANDO NO ESTÁ SEGURA, Y ESO ES LO IMPORTANTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `sugerir()` solo devuelve una cuenta cuando el nombre se parece mucho —el
 * umbral está medido en `pruebas/balance.test.ts`, no razonado—. Por debajo,
 * el desplegable llega en «elegir» a propósito.
 *
 * Rellenar los noventa con la mejor aproximación se vería mucho mejor y sería
 * peor: **nadie revisa lo que ya viene lleno**, y una caja chica mapeada a caja
 * general mueve dinero de sitio sin que nada se queje, porque el asiento cuadra
 * exactamente igual.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ Y LO QUE QUEDA SIN MAPEAR SE DICE EN VOZ ALTA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Una fila sin cuenta nuestra no entra en la apertura. Como se cae con su debe
 * y su haber juntos, **el asiento sigue cuadrando**: no hay ninguna
 * comprobación posterior que atrape un saldo que se quedó fuera. Por eso el
 * botón se apaga mientras quede una sin resolver, y el número de las que faltan
 * está a la vista todo el tiempo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ DE DÓNDE VINO CADA SALDO SE GUARDA EN EL ASIENTO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El `detalle` de cada línea lleva el código y el nombre de la cuenta de ELLOS.
 * Dentro de un año, cuando alguien pregunte por qué la 1101 abrió con ese
 * saldo, el mayor lo dice sin que haya que buscar el archivo.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';
import { leerBalance, sugerir, type FilaDeBalance } from '../nucleo/balance.ts';
import {
  cargarApertura, guardarMapeo, traerCatalogo, traerMapeo, FalloDeApi,
  type CuentaReal, type ModoDeApertura, type Pedir,
} from '../datos/libros.ts';

/** Las que pueden recibir un saldo: de movimiento y activas. */
function paraElegir(cuentas: readonly CuentaReal[]): CuentaReal[] {
  return cuentas.filter((c) => c.permiteMovimiento && c.activa);
}

/**
 * Una cuenta de resultado se reconoce por el primer dígito, igual que en la base.
 *
 * ▶ Son los grupos 4 a 9, no el 4 y el 5.
 *
 * Con la lista corta, un balance cuyos únicos gastos fueran del grupo 6
 * —sueldos, alquiler— no disparaba la pregunta de los resultados: la pantalla
 * se quedaba callada y `modoEfectivo` caía en `limpio` sin que nadie lo
 * eligiera. La base tenía el mismo hueco y se arregló en la 0046.
 */
function esDeResultado(codigo: string): boolean {
  const grupo = codigo.charAt(0);
  return grupo >= '4' && grupo <= '9';
}

export function DesdeBalance(
  { pedir, fecha, escribe, alCargar }: {
    pedir: Pedir;
    fecha: string;
    escribe: boolean;
    alCargar: (numero: string) => void;
  },
) {
  const { t, idioma } = useIdioma();

  const [cuentas, setCuentas] = useState<CuentaReal[]>([]);
  const [csv, setCsv] = useState('');
  /** De su código al nuestro. `''` es «sin resolver», y no es lo mismo que ausente. */
  const [mapeo, setMapeo] = useState<Record<string, string>>({});
  const [guardado, setGuardado] = useState<Record<string, string>>({});
  const [sugeridas, setSugeridas] = useState<Set<string>>(new Set());
  const [modo, setModo] = useState<ModoDeApertura | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  /**
   * Espejos de `mapeo` y `sugeridas`, para leerlos dentro del efecto sin
   * meterlos en sus dependencias —que lo haría dispararse a sí mismo—.
   *
   * Hacen falta desde que el CSV se puede editar a mano: el efecto corre en
   * cada tecla, y sin leer lo ya elegido, corregir una coma borraba las
   * correspondencias que la persona acababa de resolver.
   */
  const elegidoRef = useRef<Record<string, string>>({});
  const sugeridasRef = useRef<Set<string>>(new Set());
  elegidoRef.current = mapeo;
  sugeridasRef.current = sugeridas;

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const [c, m] = await Promise.all([traerCatalogo(pedir), traerMapeo(pedir)]);
        if (!vivo) return;
        setCuentas(c);
        // El mapeo que ya se guardó una vez. Que exista es la razón de ser de
        // la tabla: volver a subir un archivo no es volver a mapearlo.
        setGuardado(Object.fromEntries(m.map((f) => [f.externo, f.cuenta])));
      } catch {
        if (vivo) setFallo('sb.conexion');
      }
    })();
    return () => { vivo = false; };
  }, [pedir]);

  /** La sugerencia por nombre para una fila, o `''` si no es firme. */
  const sugerido = (f: FilaDeBalance) => sugerir(f.nombre, elegibles.map((c) => ({
    codigo: c.codigo, nombre: idioma === 'en' ? c.nombreEn : c.nombre,
  }))) ?? '';

  const lectura = useMemo(
    () => (csv.trim() === '' ? null : leerBalance(csv)), [csv]);
  const filas: readonly FilaDeBalance[] = lectura?.ok ? lectura.filas : [];
  const elegibles = useMemo(() => paraElegir(cuentas), [cuentas]);

  /**
   * Al leer el archivo se propone, y el orden importa:
   *
   *   1. lo que la persona YA resolvió en esta pantalla
   *   2. lo que se guardó la vez pasada
   *   3. una sugerencia por nombre, y solo si es firme
   *
   * ▶ EL PRIMER PUNTO ES NUEVO, Y LO PIDE LA CAJA DE PEGAR.
   *
   * Esto se dispara con cada cambio del texto. Mientras el CSV solo llegaba
   * por archivo eso pasaba una vez, pero al poder **editarlo a mano** pasa en
   * cada tecla: sin conservar lo elegido, corregir una coma borraba las
   * catorce correspondencias que la persona acababa de resolver.
   *
   * Y conservarlo también arregla el caso del archivo: volver a subir el mismo
   * balance con una fila corregida ya no obliga a mapear todo otra vez.
   *
   * Solo se conserva lo que sigue existiendo: una cuenta que desapareció del
   * archivo se va con su elección, porque guardarla sería mapear algo que ya
   * nadie trae.
   */
  useEffect(() => {
    if (filas.length === 0) return;

    // Lo ya elegido se lee de una REFERENCIA y no del estado: ponerlo en las
    // dependencias haría que este efecto se dispare a sí mismo sin parar.
    const elegido = elegidoRef.current;
    const propuesto: Record<string, string> = {};
    const deNombre = new Set<string>();

    for (const f of filas) {
      if (elegido[f.codigo]) {
        // Ya resuelta en esta pantalla: se respeta, y deja de ser sugerencia
        // aunque el parecido siga proponiendo lo mismo.
        propuesto[f.codigo] = elegido[f.codigo]!;
        if (sugeridasRef.current.has(f.codigo)) deNombre.add(f.codigo);
        continue;
      }
      if (guardado[f.codigo]) {
        // Del mapeo guardado: es una decisión tomada, no una sugerencia.
        propuesto[f.codigo] = guardado[f.codigo]!;
        continue;
      }
      const s = sugerido(f);
      propuesto[f.codigo] = s;
      if (s !== '') deNombre.add(f.codigo);
    }

    setMapeo(propuesto);
    setSugeridas(deNombre);
    // `filas` se recalcula con `csv`; `elegibles` con `cuentas`. Ese es el
    // disparo que se quiere: un archivo nuevo o editado, o el catálogo que
    // terminó de llegar.
  }, [csv, elegibles, guardado, idioma]); // eslint-disable-line react-hooks/exhaustive-deps

  const sinResolver = filas.filter((f) => !mapeo[f.codigo]).length;
  const conResultado = filas.some((f) => {
    const c = mapeo[f.codigo];
    return c !== undefined && c !== '' && esDeResultado(c);
  });

  // ▶ Sin cuentas de resultado los dos modos hacen exactamente lo mismo.
  //
  // `apertura_desde_balance` solo agrega la línea de utilidades acumuladas si
  // el neto de los grupos 4 a 9 es distinto de cero, así que con un balance sin
  // ninguna cuenta de resultado el asiento sale idéntico por los dos caminos.
  // Elegir acá no es un defecto que se disfraza de respuesta: es que no hay dos
  // respuestas.
  const modoEfectivo: ModoDeApertura | null = conResultado ? modo : 'limpio';

  const puedeMandar = escribe && !ocupado && filas.length > 0
    && sinResolver === 0 && modoEfectivo !== null;

  async function leerArchivo(f: File | null | undefined) {
    if (!f) return;
    setCsv(await f.text());
  }

  const decir = useCallback((e: unknown) => {
    const f = e instanceof FalloDeApi ? e : null;
    setFallo(f?.clase === 'conexion' ? 'sb.conexion'
      : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
  }, []);

  /** Guardar las correspondencias sin cargar nada. Se puede hacer a medias. */
  async function guardar() {
    setOcupado(true);
    setFallo(null);
    setAviso(null);
    try {
      const n = await guardarMapeo(pedir, filas
        .filter((f) => mapeo[f.codigo])
        .map((f) => ({ externo: f.codigo, nombre: f.nombre, cuenta: mapeo[f.codigo]! })));
      setGuardado(Object.fromEntries(filas
        .filter((f) => mapeo[f.codigo]).map((f) => [f.codigo, mapeo[f.codigo]!])));
      setAviso(`${t('mp.saved')} — ${n} ${t('mp.resolved')}`);
    } catch (e) {
      decir(e);
    } finally {
      setOcupado(false);
    }
  }

  async function mandar() {
    if (modoEfectivo === null) return;
    setOcupado(true);
    setFallo(null);
    try {
      // Se guarda el mapeo ANTES de cargar. Si la apertura falla, lo que el
      // contador resolvió no se pierde con el error.
      await guardarMapeo(pedir, filas
        .filter((f) => mapeo[f.codigo])
        .map((f) => ({ externo: f.codigo, nombre: f.nombre, cuenta: mapeo[f.codigo]! })));

      const numero = await cargarApertura(pedir, {
        fecha,
        modo: modoEfectivo,
        // ▶ Los importes viajan como TEXTO, tal cual salieron del archivo.
        // Las dos columnas van juntas y las netea la base: restar acá sería
        // aritmética de dinero en el navegador, que es lo que §6.1 prohíbe.
        lineas: filas.filter((f) => mapeo[f.codigo]).map((f) => ({
          cuenta: mapeo[f.codigo]!,
          debe: f.debe,
          haber: f.haber,
          detalle: f.nombre ? `${f.codigo} · ${f.nombre}` : f.codigo,
        })),
      });
      setCsv('');
      alCargar(numero);
    } catch (e) {
      decir(e);
    } finally {
      setOcupado(false);
    }
  }

  const nombreDe = (c: CuentaReal) => (idioma === 'en' ? c.nombreEn : c.nombre);

  return (
    <>
      <section className="card" style={{ marginBottom: 'var(--s6)' }}>
        <label className="field">
          <span className="field__label">{t('mp.file')}</span>
          <input
            className="input" type="file" accept=".csv,text/csv"
            onChange={(e) => void leerArchivo(e.target.files?.[0])}
          />
          <span className="field__hint">{t('mp.columns')}</span>
        </label>

        {/* ▶ La caja de pegar, que la vía de la plantilla tenía y ésta no.
         *
         * Un balance exportado de otro sistema llega con lo que ese sistema
         * decida meterle: una fila de totales al final, una cabecera partida en
         * dos, una cuenta sin código. Sin esto, arreglar una coma obliga a
         * volver al sistema anterior, exportar de nuevo y empezar el mapeo
         * desde cero — y es justo el trabajo que esta pantalla existe para
         * quitar. */}
        <label className="field" style={{ marginTop: 'var(--s5)' }}>
          <span className="field__label">{t('mp.paste')}</span>
          <textarea
            className="input input--mono" rows={6} value={csv}
            onChange={(e) => setCsv(e.target.value)}
          />
          <span className="field__hint">{t('mp.pasteHint')}</span>
        </label>
      </section>

      {fallo && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(fallo as never)}</span>
        </div>
      )}
      {aviso && (
        <div className="alert" style={{ marginBottom: 'var(--s6)' }}><span>{aviso}</span></div>
      )}

      {lectura && !lectura.ok && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          {/* El lector devuelve una CLAVE. La frase es de este diccionario y
              existe en los dos idiomas. */}
          <span>
            {lectura.fila ? `${t('ap.row')} ${lectura.fila}: ` : ''}
            {t(`mp.err.${lectura.motivo}` as never)}
            {lectura.dato ? ` — ${lectura.dato}` : ''}
          </span>
        </div>
      )}

      {lectura?.ok && (
        <section className="card card--pad0" style={{ marginBottom: 'var(--s6)' }}>
          <div className="card__head">
            <h3>{t('mp.title')}</h3>
            <span className="spacer" />
            {sinResolver > 0
              ? <span className="chip chip--warn">{sinResolver} {t('mp.pending')}</span>
              : (
                <span className="chip chip--ok">
                  {filas.length === 1
                    ? t('mp.mappedOne')
                    : t('mp.mapped', { cuentas: String(filas.length) })}
                </span>
              )}
          </div>
          <p className="small muted" style={{ padding: '0 var(--s5)' }}>{t('mp.sub')}</p>

          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('mp.theirs')}</th>
                  <th style={{ textAlign: 'right' }}>{t('ap.debit')}</th>
                  <th style={{ textAlign: 'right' }}>{t('ap.credit')}</th>
                  <th>{t('mp.ours')}</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr key={f.codigo}>
                    <td>
                      <span className="mono">{f.codigo}</span>
                      {f.nombre && <span className="small muted"> · {f.nombre}</span>}
                    </td>
                    {/* Tal como vinieron: enseñarlos bonito escondería un
                        `1.250,00` leído como `1,250.00`. */}
                    <td className="mono" style={{ textAlign: 'right' }}>{f.debe}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{f.haber}</td>
                    <td>
                      <select
                        className="input input--mono"
                        value={mapeo[f.codigo] ?? ''}
                        disabled={!escribe}
                        onChange={(e) => {
                          const v = e.target.value;
                          setMapeo((m) => ({ ...m, [f.codigo]: v }));
                          // Tocada a mano deja de ser una sugerencia: el aviso
                          // de «revísela» ya no aplica a esta fila.
                          setSugeridas((s) => {
                            const n = new Set(s); n.delete(f.codigo); return n;
                          });
                        }}
                      >
                        <option value="">{t('mp.pick')}</option>
                        {elegibles.map((c) => (
                          <option key={c.codigo} value={c.codigo}>
                            {c.codigo} · {nombreDe(c)}
                          </option>
                        ))}
                      </select>
                      {sugeridas.has(f.codigo) && (
                        <span className="small muted">{t('mp.suggested')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card__foot">
            <button
              className="btn" type="button" disabled={!escribe || ocupado}
              onClick={() => void guardar()}
            >
              {t('mp.save')}
            </button>
            <span className="small muted">
              {sugeridas.size > 0 ? t('mp.suggestedWhy') : t('mp.provenance')}
            </span>
          </div>
        </section>
      )}

      {lectura?.ok && sinResolver > 0 && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t('mp.pendingWhy')}</span>
        </div>
      )}

      {/* ── La pregunta de los resultados, solo cuando hay algo que decidir ── */}
      {lectura?.ok && sinResolver === 0 && (
        <section className="card" style={{ marginBottom: 'var(--s6)' }}>
          {!conResultado ? (
            <p className="small muted">{t('mp.modeNone')}</p>
          ) : (
            <>
              <div className="card__head"><h3>{t('mp.mode')}</h3></div>
              <p className="small muted">{t('mp.modeWhy')}</p>
              <div className="grid grid--2" style={{ gap: 'var(--s5)', marginTop: 'var(--s5)' }}>
                {([
                  ['limpio', 'mp.modeClean', 'mp.modeCleanWhy'],
                  ['mitad_de_ano', 'mp.modeMid', 'mp.modeMidWhy'],
                ] as const).map(([valor, titulo, porque]) => (
                  <label
                    key={valor}
                    className={modo === valor ? 'card card--sel' : 'card'}
                    style={{ cursor: 'pointer' }}
                  >
                    <span style={{ display: 'flex', gap: 'var(--s3)', alignItems: 'center' }}>
                      <input
                        type="radio" name="modo" value={valor}
                        checked={modo === valor} disabled={!escribe}
                        onChange={() => setModo(valor)}
                      />
                      <strong>{t(titulo)}</strong>
                    </span>
                    <p className="small muted">{t(porque)}</p>
                  </label>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      <div className="page-actions">
        <button
          className="btn btn--primary" type="button"
          disabled={!puedeMandar} onClick={() => void mandar()}
        >
          {t('ap.load')}
        </button>
        <span className="small muted">{t('ap.onlyOnce')}</span>
      </div>
    </>
  );
}
